import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarClock, ExternalLink, HardHat, Mail, MapPin, Phone, RefreshCw, Store } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { PageContainer } from '../../../common/components/PageContainer';
import { DeskPatch, DeskState, LEAD_DESK_STAGES, LeadDeskStage, LeadFile, useLeadDeskApi } from '../../../common/hooks/use-lead-desk-api.hook';
import { RelativeTime, SectionCard, StatusBadge, formatCurrency, formatDate, formatNumber } from '../../../components/design-system/admin';
import { MiniStat, Thumb } from '../../../components/design-system/analytics';
import { MediaLightbox, PicturePlaceholder } from '../../../components/design-system/common';
import { FollowUpComposer } from '../../../modules/vendor/lead-desk/components/FollowUpComposer';
import { LeadConversation } from '../../../modules/vendor/lead-desk/components/LeadConversation';
import { LeadTierBadge } from '../../../modules/vendor/lead-desk/components/LeadTierBadge';
import { LeadTimeline } from '../../../modules/vendor/lead-desk/components/LeadTimeline';
import { displayName, initialOf } from '../../../modules/vendor/lead-desk/lead-desk-view';

const STRONG = new Set(['WANTS_TO_BUY', 'REQUESTED_VISIT', 'REQUESTED_QUOTE', 'REQUESTED_SAMPLE', 'REQUESTED_CALLBACK']);

/** A label and its value, as every fact on the file reads. */
function Fact({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
    return (
        <div className="min-w-0">
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="mt-0.5 text-sm">{children}</dd>
        </div>
    );
}

const toDateInput = (iso: string | null | undefined) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

/**
 * One lead, as a file: who they are and how warm, their room with the product
 * in it, what they looked at and asked for, how big the job is and what it is
 * worth, the store nearest them, what they said to the shop's assistant, what
 * they did — and the follow-up, written by Ardi and sent in one tap.
 */
export const LeadFilePage = () => {
    const { t } = useTranslation();
    const { leadId = '' } = useParams();
    const api = useLeadDeskApi();
    const [lead, setLead] = useState<LeadFile | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<'locked' | 'missing' | 'failed' | null>(null);
    const [image, setImage] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState<string | null>(null);
    const [note, setNote] = useState('');
    const [valueInput, setValueInput] = useState('');
    const [lostReason, setLostReason] = useState('');
    const [wonAmount, setWonAmount] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const file = await api.file(leadId);
            setLead(file);
            setLostReason(file.desk.lostReason ?? '');
            setWonAmount(file.desk.wonAmount != null ? String(file.desk.wonAmount) : '');
            setValueInput(file.desk.value?.source === 'manual' ? String(file.desk.value.amount) : '');
        } catch (e: any) {
            const status = e?.status ?? e?.response?.status;
            setError(status === 403 ? 'locked' : status === 404 ? 'missing' : 'failed');
        } finally {
            setLoading(false);
        }
    }, [api, leadId]);

    useEffect(() => {
        load();
    }, [load]);

    const applyDesk = (desk: DeskState) => setLead((l) => (l ? { ...l, desk } : l));

    const save = async (patch: DeskPatch, message?: string) => {
        if (!lead) return;
        setSaving(true);
        setSaved(null);
        try {
            const desk = await api.patchDesk(lead.id, patch);
            applyDesk(desk);
            setSaved(message ?? t('LEAD_DESK.FILE.SAVED'));
            return desk;
        } catch {
            setSaved(null);
        } finally {
            setSaving(false);
        }
        return undefined;
    };

    const name = lead ? displayName({ name: lead.contact.name, email: lead.email }) : '';
    const currency = lead?.value?.currency ?? lead?.products.find((p) => p.price)?.price?.currency ?? 'EUR';
    const productTitle = (id: string) => lead?.products.find((p) => p.productId === id)?.title ?? null;
    const render = lead?.images.renders[0];
    const room = lead?.images.rooms[0];

    return (
        <PageContainer breadcrumbs={[{ label: t('R2R.WORKSPACE.SALES') }, { label: t('R2R.NAV.DESK'), href: '/sales/desk' }, { label: name || '…' }]}>
            <div className="mx-auto flex w-full min-w-0 max-w-[1240px] flex-col gap-5" data-testid="lead-file">
                <div className="flex items-center justify-between gap-3">
                    <Button asChild variant="ghost" size="sm" className="-ms-2">
                        <Link to="/sales/desk">
                            <ArrowLeft className="rtl:-scale-x-100" />
                            {t('LEAD_DESK.BACK_TO_DESK')}
                        </Link>
                    </Button>
                    <Button variant="outline" size="sm" className="h-8" onClick={load} disabled={loading} aria-label={t('LEAD_DESK.REFRESH')}>
                        <RefreshCw className={cn(loading && 'animate-spin')} />
                    </Button>
                </div>

                {loading && !lead && (
                    <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                        <Skeleton className="h-[520px] rounded-xl" />
                        <Skeleton className="h-[520px] rounded-xl" />
                    </div>
                )}

                {error && (
                    <Card>
                        <CardContent className="flex flex-wrap items-center justify-between gap-3">
                            <p className="text-sm text-muted-foreground">
                                {error === 'locked' ? t('LEAD_DESK.FILE.LOCKED') : error === 'missing' ? t('LEAD_DESK.FILE.NOT_FOUND') : t('LEAD_DESK.FILE.LOAD_FAILED')}
                            </p>
                            {error === 'locked' ? (
                                <Button asChild variant="outline" size="sm"><Link to="/home/billing/plan">{t('LEAD_DESK.SEE_PLANS')}</Link></Button>
                            ) : error === 'failed' ? (
                                <Button variant="outline" size="sm" onClick={load}>{t('LEAD_DESK.RETRY')}</Button>
                            ) : null}
                        </CardContent>
                    </Card>
                )}

                {lead && (
                    <>
                        {/* Who, how warm and why, where they are in the pipeline. */}
                        <Card className="gap-0 py-0" data-testid="lead-header">
                            <CardContent className="flex flex-col gap-4 p-5 md:flex-row md:items-start md:justify-between">
                                <div className="flex min-w-0 items-start gap-4">
                                    <span className="flex size-12 shrink-0 items-center justify-center rounded-full border bg-muted text-base font-medium" aria-hidden>
                                        {initialOf({ name: lead.contact.name, email: lead.email })}
                                    </span>
                                    <div className="min-w-0">
                                        <h1 className="flex flex-wrap items-center gap-2 font-heading text-[22px] font-medium tracking-tight">
                                            <span className="truncate">{name}</span>
                                            {lead.audience === 'trade' && (
                                                <span className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-normal">
                                                    <HardHat className="size-3" aria-hidden />{t('LEAD_DESK.AUDIENCE_TRADE')}
                                                </span>
                                            )}
                                        </h1>
                                        <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                                            <span className="inline-flex items-center gap-1.5"><Mail className="size-3.5" aria-hidden />{lead.email}</span>
                                            {lead.contact.phone && <span className="inline-flex items-center gap-1.5" dir="ltr"><Phone className="size-3.5" aria-hidden />{lead.contact.phone}</span>}
                                            {lead.visitorLocation?.city && <span className="inline-flex items-center gap-1.5"><MapPin className="size-3.5" aria-hidden />{lead.visitorLocation.city}</span>}
                                        </p>
                                        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                                            <LeadTierBadge tier={lead.score.tier} score={lead.score.value} />
                                            {lead.score.reasons.slice(0, 5).map((r) => (
                                                <StatusBadge key={r} tone={STRONG.has(r) ? 'info' : 'neutral'} className={STRONG.has(r) ? 'font-medium' : 'text-muted-foreground'}>
                                                    {t(`LEAD_DESK.REASON.${r}`, { defaultValue: r })}
                                                </StatusBadge>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex shrink-0 flex-col gap-1.5 md:items-end">
                                    <label className="text-xs text-muted-foreground" htmlFor="lead-stage">{t('LEAD_DESK.FILE.STAGE')}</label>
                                    <NativeSelect
                                        id="lead-stage"
                                        value={lead.desk.stage}
                                        disabled={saving}
                                        onChange={(e) => {
                                            const stage = e.target.value as LeadDeskStage;
                                            save({ stage }, t('LEAD_DESK.FILE.STAGE_SAVED', { stage: t(`LEAD_DESK.STAGE.${stage.toUpperCase()}`) }));
                                        }}
                                        data-testid="lead-stage"
                                    >
                                        {LEAD_DESK_STAGES.map((s) => (
                                            <NativeSelectOption key={s} value={s}>{t(`LEAD_DESK.STAGE.${s.toUpperCase()}`)}</NativeSelectOption>
                                        ))}
                                    </NativeSelect>
                                    <span className="text-xs text-muted-foreground"><RelativeTime value={lead.desk.stageAt} /></span>
                                    {saved && <span className="text-xs text-success" role="status">{saved}</span>}
                                </div>
                            </CardContent>
                        </Card>

                        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                            <div className="flex min-w-0 flex-col gap-5">
                                {/* Their room, and their room with the product in it — large. */}
                                <SectionCard title={t('LEAD_DESK.FILE.PICTURES')} testId="lead-pictures">
                                    {render || room ? (
                                        <div className={cn('grid gap-3', render && room && 'sm:grid-cols-2')}>
                                            {[render && { ...render, label: t('LEAD_DESK.FILE.RENDER') }, room && { ...room, label: t('LEAD_DESK.FILE.ROOM') }]
                                                .filter(Boolean)
                                                .map((img: any) => (
                                                    <figure key={img.url} className="flex min-w-0 flex-col gap-1.5">
                                                        <button type="button" onClick={() => setImage(img.url)} className="overflow-hidden rounded-xl border focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
                                                            <img src={img.url} alt={img.label} className="aspect-[4/3] w-full object-cover" />
                                                        </button>
                                                        <figcaption className="text-xs text-muted-foreground">
                                                            {img.label} · {t(`LEAD_DESK.FILE.SOURCE.${String(img.source).toUpperCase()}`, { defaultValue: img.source })}
                                                            {img.at && <> · <RelativeTime value={img.at} /></>}
                                                        </figcaption>
                                                    </figure>
                                                ))}
                                        </div>
                                    ) : (
                                        // Only pictures the shopper gave the shop reach this page (a request, a shared saved room).
                                        <PicturePlaceholder
                                            size="lg"
                                            reason="none"
                                            label={t('LEAD_DESK.FILE.NO_ROOM_PICTURE')}
                                            hint={t('LEAD_DESK.FILE.NO_ROOM_PICTURE_HINT')}
                                            className="max-h-56"
                                            testId="lead-no-room-picture"
                                        />
                                    )}
                                    {lead.images.renders.length + lead.images.rooms.length > 2 && (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {[...lead.images.renders.slice(1), ...lead.images.rooms.slice(1)].map((img) => (
                                                <button key={img.url} type="button" onClick={() => setImage(img.url)} className="overflow-hidden rounded-lg border">
                                                    <img src={img.url} alt="" className="h-16 w-24 object-cover" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </SectionCard>

                                <FollowUpComposer lead={lead} onSent={applyDesk} />

                                {lead.threads.length > 0 && (
                                    <SectionCard title={t('LEAD_DESK.FILE.CHAT')} testId="lead-chat">
                                        <LeadConversation threads={lead.threads} onImage={setImage} />
                                    </SectionCard>
                                )}

                                <SectionCard title={t('LEAD_DESK.FILE.TIMELINE')}>
                                    <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                                        <MiniStat label={t('LEAD_DESK.FILE.STATS.DOWNLOADS')} value={formatNumber(lead.activity.downloads)} />
                                        <MiniStat label={t('LEAD_DESK.FILE.STATS.EMAILS')} value={formatNumber(lead.activity.emailsSent)} />
                                        <MiniStat label={t('LEAD_DESK.FILE.STATS.ACTIVE_DAYS')} value={formatNumber(lead.activity.activeDays30)} />
                                        <MiniStat label={t('LEAD_DESK.FILE.STATS.FIRST_SEEN')} value={formatDate(lead.firstSeenAt)} />
                                    </div>
                                    <LeadTimeline entries={lead.timeline} productTitle={productTitle} />
                                </SectionCard>
                            </div>

                            <div className="flex min-w-0 flex-col gap-5">
                                {/* The job: how big, what it is worth, where they can see it. */}
                                <SectionCard title={t('LEAD_DESK.FILE.PROJECT')} testId="lead-project">
                                    <dl className="flex flex-col gap-4">
                                        <Fact label={t('LEAD_DESK.FILE.AREA')}>
                                            {lead.area ? (
                                                <>
                                                    <span className="font-heading text-lg font-medium tabular-nums">{t('LEAD_DESK.FILE.AREA_VALUE', { value: formatNumber(lead.area.m2, 1) })}</span>
                                                    <span className="block text-xs text-muted-foreground">
                                                        {[lead.area.range ? t('LEAD_DESK.FILE.AREA_RANGE', { min: formatNumber(lead.area.range[0], 1), max: formatNumber(lead.area.range[1], 1) }) : null,
                                                            lead.area.source ? t(`LEAD_DESK.FILE.AREA_SOURCE.${lead.area.source.toUpperCase()}`, { defaultValue: '' }) : null].filter(Boolean).join(' · ')}
                                                    </span>
                                                </>
                                            ) : '—'}
                                        </Fact>
                                        <Fact label={t('LEAD_DESK.FILE.VALUE')}>
                                            {lead.value ? (
                                                <>
                                                    <span className="font-heading text-lg font-medium tabular-nums">{formatCurrency(lead.value.amount, lead.value.currency, 0)}</span>
                                                    <span className="block text-xs text-muted-foreground">{t(`LEAD_DESK.FILE.VALUE_SOURCE.${lead.value.source.toUpperCase()}`, { defaultValue: lead.value.source })}</span>
                                                </>
                                            ) : (
                                                <span className="text-muted-foreground">{t('LEAD_DESK.FILE.NO_VALUE')}</span>
                                            )}
                                            <form
                                                className="mt-2 flex items-center gap-2"
                                                onSubmit={(e) => {
                                                    e.preventDefault();
                                                    const amount = Number(valueInput.replace(',', '.'));
                                                    if (Number.isFinite(amount) && amount >= 0 && valueInput.trim()) save({ value: { amount, currency } }).then(load);
                                                }}
                                            >
                                                <Input value={valueInput} onChange={(e) => setValueInput(e.target.value)} inputMode="decimal" placeholder={t('LEAD_DESK.FILE.VALUE_PLACEHOLDER')} aria-label={t('LEAD_DESK.FILE.VALUE_EDIT')} className="h-8 w-28" />
                                                <span className="text-xs text-muted-foreground">{currency}</span>
                                                <Button type="submit" variant="outline" size="sm" disabled={saving || !valueInput.trim()}>{t('LEAD_DESK.FILE.SAVE')}</Button>
                                                {lead.desk.value?.source === 'manual' && (
                                                    <Button type="button" variant="ghost" size="sm" onClick={() => { setValueInput(''); save({ value: null }).then(load); }}>
                                                        {t('LEAD_DESK.FILE.VALUE_CLEAR')}
                                                    </Button>
                                                )}
                                            </form>
                                        </Fact>
                                        <Fact label={t('LEAD_DESK.FILE.STORE')}>
                                            {lead.nearestStore ? (
                                                <div className="flex items-start gap-2">
                                                    <Store className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                                                    <div className="min-w-0">
                                                        <p className="font-medium">{lead.nearestStore.name}</p>
                                                        {lead.nearestStore.address && <p className="text-xs text-muted-foreground">{lead.nearestStore.address}</p>}
                                                        <p className="text-xs text-muted-foreground">
                                                            {[lead.nearestStore.distanceKm != null ? t('LEAD_DESK.FILE.STORE_DISTANCE', { km: formatNumber(lead.nearestStore.distanceKm, 0) }) : null,
                                                                lead.nearestStore.basis === 'request' ? t('LEAD_DESK.FILE.STORE_BASIS_REQUEST') : t('LEAD_DESK.FILE.STORE_BASIS_VISIT'),
                                                                lead.nearestStore.hours || null].filter(Boolean).join(' · ')}
                                                        </p>
                                                        {lead.nearestStore.bookingUrl && (
                                                            <a href={lead.nearestStore.bookingUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs underline underline-offset-4">
                                                                {t('LEAD_DESK.FILE.STORE_BOOK')}<ExternalLink className="size-3" aria-hidden />
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground">{t('LEAD_DESK.FILE.NO_STORE')}</span>
                                            )}
                                        </Fact>
                                    </dl>
                                </SectionCard>

                                {/* The desk: next step, the result, notes for the team. */}
                                <SectionCard title={t('LEAD_DESK.FILE.DESK')} testId="lead-desk-state">
                                    <div className="flex flex-col gap-4">
                                        <label className="flex flex-col gap-1.5">
                                            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><CalendarClock className="size-3.5" aria-hidden />{t('LEAD_DESK.FILE.NEXT_STEP')}</span>
                                            <div className="flex items-center gap-2">
                                                <Input
                                                    type="date"
                                                    className="h-8 w-44"
                                                    value={toDateInput(lead.desk.nextStepAt)}
                                                    onChange={(e) => save({ nextStepAt: e.target.value ? new Date(`${e.target.value}T09:00:00`).toISOString() : null })}
                                                />
                                                {lead.desk.nextStepAt && (
                                                    <Button variant="ghost" size="sm" onClick={() => save({ nextStepAt: null })}>{t('LEAD_DESK.FILE.NEXT_STEP_CLEAR')}</Button>
                                                )}
                                            </div>
                                        </label>

                                        {lead.desk.stage === 'won' && (
                                            <form className="flex flex-col gap-1.5" onSubmit={(e) => { e.preventDefault(); const v = Number(wonAmount.replace(',', '.')); if (Number.isFinite(v)) save({ wonAmount: v }); }}>
                                                <span className="text-xs text-muted-foreground">{t('LEAD_DESK.FILE.WON_AMOUNT')}</span>
                                                <div className="flex items-center gap-2">
                                                    <Input value={wonAmount} onChange={(e) => setWonAmount(e.target.value)} inputMode="decimal" className="h-8 w-32" aria-label={t('LEAD_DESK.FILE.WON_AMOUNT')} />
                                                    <span className="text-xs text-muted-foreground">{currency}</span>
                                                    <Button type="submit" variant="outline" size="sm" disabled={saving}>{t('LEAD_DESK.FILE.SAVE')}</Button>
                                                </div>
                                            </form>
                                        )}

                                        {lead.desk.stage === 'lost' && (
                                            <form className="flex flex-col gap-1.5" onSubmit={(e) => { e.preventDefault(); save({ lostReason: lostReason.trim() || null }); }}>
                                                <span className="text-xs text-muted-foreground">{t('LEAD_DESK.FILE.LOST_REASON')}</span>
                                                <div className="flex items-center gap-2">
                                                    <Input value={lostReason} onChange={(e) => setLostReason(e.target.value)} placeholder={t('LEAD_DESK.FILE.LOST_PLACEHOLDER')} className="h-8" aria-label={t('LEAD_DESK.FILE.LOST_REASON')} />
                                                    <Button type="submit" variant="outline" size="sm" disabled={saving}>{t('LEAD_DESK.FILE.SAVE')}</Button>
                                                </div>
                                            </form>
                                        )}

                                        <div className="flex flex-col gap-2">
                                            <span className="text-xs text-muted-foreground">{t('LEAD_DESK.FILE.NOTES')}</span>
                                            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('LEAD_DESK.FILE.NOTE_PLACEHOLDER')} rows={3} className="text-sm" />
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="self-start"
                                                disabled={saving || !note.trim()}
                                                onClick={() => save({ note }).then((d) => d && setNote(''))}
                                            >
                                                {t('LEAD_DESK.FILE.ADD_NOTE')}
                                            </Button>
                                            {lead.desk.notes.length ? (
                                                <ul className="flex flex-col gap-2">
                                                    {lead.desk.notes.map((n, i) => (
                                                        <li key={i} className="rounded-lg border px-3 py-2 text-sm">
                                                            <p className="whitespace-pre-wrap">{n.text}</p>
                                                            <p className="mt-1 text-xs text-muted-foreground"><RelativeTime value={n.at} /></p>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-xs text-muted-foreground">{t('LEAD_DESK.FILE.NO_NOTES')}</p>
                                            )}
                                        </div>
                                    </div>
                                </SectionCard>

                                <SectionCard title={t('LEAD_DESK.FILE.REQUESTS')} testId="lead-requests">
                                    {lead.requests.length ? (
                                        <ul className="flex flex-col gap-3">
                                            {lead.requests.map((r) => (
                                                <li key={r.uuid} className="rounded-lg border p-3">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="text-sm font-medium">{t(`LEAD_DESK.REQUEST.${r.kind.toUpperCase()}`, { defaultValue: r.kind })}</span>
                                                        <RelativeTime value={r.createdAt} className="text-xs text-muted-foreground" />
                                                    </div>
                                                    {r.items.slice(0, 3).map((item, i) => (
                                                        <p key={i} className="mt-1 truncate text-xs text-muted-foreground">
                                                            {[item.title, item.areaM2 ? t('LEAD_DESK.AREA', { value: formatNumber(item.areaM2, 1) }) : null,
                                                                item.total != null && r.currency ? formatCurrency(item.total, r.currency, 0) : null].filter(Boolean).join(' · ')}
                                                        </p>
                                                    ))}
                                                    {(r.store || r.preferredDate) && (
                                                        <p className="mt-1 text-xs text-muted-foreground">
                                                            {[r.store?.name, r.preferredDate ? t('LEAD_DESK.FILE.PREFERRED_DATE', { date: formatDate(r.preferredDate) }) : null].filter(Boolean).join(' · ')}
                                                        </p>
                                                    )}
                                                    {r.message && <p className="mt-2 text-sm italic">“{r.message}”</p>}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">{t('LEAD_DESK.FILE.NO_REQUESTS')}</p>
                                    )}
                                </SectionCard>

                                <SectionCard title={t('LEAD_DESK.FILE.PRODUCTS')} testId="lead-products">
                                    {lead.products.length ? (
                                        <ul className="flex flex-col divide-y">
                                            {lead.products.map((p) => (
                                                <li key={p.productId} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                                                    <Thumb src={p.thumbnailPath} alt={p.title} size="md" />
                                                    <div className="min-w-0 flex-1">
                                                        <p className="truncate text-sm">{p.link ? <a href={p.link} target="_blank" rel="noreferrer" className="hover:underline">{p.title}</a> : p.title}</p>
                                                        <p className="text-xs text-muted-foreground">{t('LEAD_DESK.FILE.TRIED', { n: p.usageCount })}</p>
                                                    </div>
                                                    <span className="shrink-0 text-end text-sm tabular-nums">
                                                        {p.price ? (
                                                            <>
                                                                {formatCurrency(p.price.amount, p.price.currency, 2)}
                                                                {p.price.unit && <span className="ms-1 text-xs text-muted-foreground">{t(`LEAD_DESK.FILE.PRICE_UNIT.${p.price.unit.toUpperCase()}`, { defaultValue: '' })}</span>}
                                                            </>
                                                        ) : (
                                                            <span className="text-xs text-muted-foreground">{t('LEAD_DESK.FILE.NO_PRICE')}</span>
                                                        )}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">{t('LEAD_DESK.FILE.NO_PRODUCTS')}</p>
                                    )}
                                </SectionCard>

                                <SectionCard title={t('LEAD_DESK.FILE.CONTACT')}>
                                    <dl className="grid grid-cols-1 gap-3">
                                        <Fact label={t('LEAD_DESK.FILE.EMAIL')}>{lead.email}</Fact>
                                        {lead.contact.phone && <Fact label={t('LEAD_DESK.FILE.PHONE')}><span dir="ltr">{lead.contact.phone}</span></Fact>}
                                        {lead.contact.preferred && <Fact label={t('LEAD_DESK.FILE.PREFERS')}>{t(`LEAD_DESK.FILE.PREFERRED.${lead.contact.preferred.toUpperCase()}`)}</Fact>}
                                        <Fact label={t('LEAD_DESK.FILE.CONSENT')}>
                                            <span className={cn('text-xs', lead.consent.unsubscribedAt ? 'text-destructive' : 'text-muted-foreground')}>
                                                {lead.consent.unsubscribedAt ? t('LEAD_DESK.FILE.UNSUBSCRIBED') : lead.consent.marketing ? t('LEAD_DESK.FILE.CONSENT_YES') : t('LEAD_DESK.FILE.CONSENT_NO')}
                                            </span>
                                        </Fact>
                                        {lead.attribution && (lead.attribution.campaign || lead.attribution.source || lead.attribution.referrer) && (
                                            <Fact label={t('LEAD_DESK.FILE.CAME_FROM')}>
                                                {[lead.attribution.source, lead.attribution.campaign].filter(Boolean).join(' · ') || lead.attribution.referrer}
                                            </Fact>
                                        )}
                                    </dl>
                                </SectionCard>
                            </div>
                        </div>
                    </>
                )}
            </div>
            <MediaLightbox media={image ? { url: image, kind: 'image' } : null} onClose={() => setImage(null)} />
        </PageContainer>
    );
};

export default LeadFilePage;
